export interface ParsedPasteRow {
  /** 1-based line number in the box the merchant pasted into. */
  line: number;
  sku: string;
  /**
   * Verbatim, never rounded. The column holds three decimal places and the
   * server owns the two-place rule (TIER_AMOUNT_TOO_MANY_DECIMALS), so rounding
   * here would hide a refusal the merchant needs to see.
   */
  amount: string;
  raw: string;
}

export interface ParsedPasteLineError {
  line: number;
  raw: string;
  reason: "columns" | "sku" | "amount" | "unknownSku";
}

export interface ParsedPaste {
  rows: ParsedPasteRow[];
  errors: ParsedPasteLineError[];
}

const AMOUNT = /^-?\d+(\.\d+)?$/;

/**
 * `sku<sep>amount` per line: a tab if the line has one, a comma otherwise, and
 * EXACTLY two fields, which is what a spreadsheet column pair pastes as. Blank
 * lines and a leading header row are dropped. Everything else comes back in
 * `errors` with its line number rather than being skipped silently.
 *
 * A semicolon refuses the line outright. A European "SKU-1;129,99" would
 * otherwise split on the decimal comma and import a price of 99.
 */
export const parsePasteInput = (input: string): ParsedPaste => {
  const rows: ParsedPasteRow[] = [];
  const errors: ParsedPasteLineError[] = [];

  // A header is only a header if it is the first line with anything on it: a
  // spreadsheet copy often carries a leading blank line, and gating on line 1
  // reported that paste's header as a malformed line.
  let seenContent = false;

  input.split(/\r?\n/).forEach((raw, index) => {
    const line = index + 1;
    const trimmed = raw.trim();

    if (!trimmed) {
      return;
    }

    const isFirstContentLine = !seenContent;

    seenContent = true;

    const fields = (trimmed.includes("\t") ? trimmed.split("\t") : trimmed.split(",")).map(field =>
      field.trim(),
    );

    if (fields.length !== 2 || trimmed.includes(";")) {
      errors.push({ line, raw: trimmed, reason: "columns" });

      return;
    }

    const [sku, amount] = fields;

    // A pasted header row: "sku, price". Dropped, not reported.
    if (isFirstContentLine && !AMOUNT.test(amount) && /^(sku|variant)$/i.test(sku)) {
      return;
    }

    if (!sku) {
      errors.push({ line, raw: trimmed, reason: "sku" });

      return;
    }

    if (!AMOUNT.test(amount)) {
      errors.push({ line, raw: trimmed, reason: "amount" });

      return;
    }

    rows.push({ line, sku, amount, raw: trimmed });
  });

  return { rows, errors };
};

/**
 * The server refuses a bulk create of more than this many rows
 * (MAX_TIER_PRICES), so the dialog says so before the merchant waits for a save.
 *
 * The cap is on the accumulated create, not on one paste: two pastes of 400 are
 * one bulkCreate of 800 and are refused just the same, so the dialog counts the
 * unsaved rows already on the grid alongside the ones this paste would add.
 */
export const PASTE_ROW_CAP = 500;

export interface PasteSummary {
  /** Lines the dialog will actually write: parsed AND resolved to a variant. */
  ready: ParsedPasteRow[];
  /** Every line that will not be written, in the order they were pasted. */
  refused: ParsedPasteLineError[];
}

/**
 * What the counter and the primary button are allowed to claim. A line whose SKU
 * no variant carries is refused HERE, in the same pass as the number check,
 * rather than counted as ready and dropped on the way to the grid.
 *
 * `knownSkus` is null until the lookup has answered for this exact paste, and
 * nothing is ready until it has: claiming a count we have not checked is the
 * defect this function exists to close.
 */
export const summarisePaste = (
  parsed: ParsedPaste,
  knownSkus: ReadonlySet<string> | null,
): PasteSummary => {
  const refused: ParsedPasteLineError[] = [...parsed.errors];

  if (!knownSkus) {
    return { ready: [], refused };
  }

  const ready: ParsedPasteRow[] = [];

  parsed.rows.forEach(row => {
    if (knownSkus.has(row.sku)) {
      ready.push(row);

      return;
    }

    refused.push({ line: row.line, raw: row.raw, reason: "unknownSku" });
  });

  return { ready, refused: refused.sort((a, b) => a.line - b.line) };
};
